// Stale-response guard for the global SearchBar (GS-H11).
//
// THE BUG (measured live on web-stg 2026-07-28, bundle main.1d538a78.js):
// `fetchData` is a bare `await` with no request identity. Every keystroke-
// debounce, tab switch and infinite-scroll page fires its own request, and
// whichever one resolves LAST wins the render — not whichever one the user
// actually asked for. Reproduction: stall the "terry" response 3s, then type
// "gordon". The network log shows
//   sent terry -> sent gordon -> resolved gordon -> resolved terry
// and the dropdown ends up showing Terry Curran / Teresa Linder while the
// input still reads "gordon". The user is looking at results for a query they
// already replaced.
//
// THE FIX: hand every request a monotonically increasing token. A response may
// only commit to state if its token is still the newest one issued. Anything
// older is a superseded query and is dropped on the floor.
//
// Kept as a pure factory (no React, no DOM) so the ordering rules can be
// unit-tested exhaustively without rendering the component, matching the
// searchKeyboardNav.js / searchAnnouncement.js pattern already used here.

/**
 * Create a monotonic request sequencer.
 *
 * @returns {{issue: () => number, isCurrent: (token: number) => boolean, current: () => number, invalidate: () => number}}
 */
export function createSearchRequestSeq() {
  let latest = 0;

  return {
    /** Claim the next token. The caller owns it for the life of its request. */
    issue() {
      latest += 1;
      return latest;
    },

    /** True only for the most recently issued token. */
    isCurrent(token) {
      return token === latest;
    },

    /** The newest token issued so far (0 before the first request). */
    current() {
      return latest;
    },

    /**
     * Supersede every in-flight request without starting a new one. Used when
     * the query is cleared (X button / emptied input): whatever is still in
     * flight must not be allowed to re-open the dropdown afterwards.
     */
    invalidate() {
      latest += 1;
      return latest;
    },
  };
}

/**
 * Pure predicate form of the same rule, for callers holding both values.
 *
 * @param {number} token  token the response was issued under
 * @param {number} latest newest token issued
 * @returns {boolean} true when the response belongs to a superseded query
 */
export function isStaleResponse(token, latest) {
  return token !== latest;
}
